/*
 * Copyright (c) 2022 automata-network/tgb-donation authors and contributors.
 * This software may be modified and distributed under the terms of MIT license.
 * Refer to the LICENSE file for details.
 */
import { PropsWithChildren, createContext, useCallback, useContext } from 'react'

import { Web3Provider as EthersWeb3Provider } from '@ethersproject/providers'
import { Web3ReactProvider, useWeb3React } from '@web3-react/core'
import { Web3ReactContextInterface } from '@web3-react/core/dist/types'
import { InjectedConnector } from '@web3-react/injected-connector'

import { SUPPORTED_CHAINS } from '../constants'

type Web3ContextType = Omit<Web3ReactContextInterface<EthersWeb3Provider>, 'activate'> & {
  activate: (onError?: (error: Error) => void, throwErrors?: boolean) => Promise<void>
}

const INITIAL_WEB3_CONTEXT: Web3ContextType = {
  active: false,
  account: undefined,
  chainId: undefined,
  connector: undefined,
  error: undefined,
  library: undefined,
  activate: () => {
    throw new Error('activate function must be overriden before use')
  },
  deactivate: () => {
    throw new Error('deactivate function must be overriden before use')
  },
  setError: () => {
    throw new Error('setError function must be overriden before use')
  }
}

const getLibrary = (provider: any = undefined) => {
  const library = new EthersWeb3Provider(provider)
  return library
}

const injectedConnector = new InjectedConnector({
  supportedChainIds: SUPPORTED_CHAINS
})

const Web3Context = createContext(INITIAL_WEB3_CONTEXT)

const Web3RootProvider = ({ children }: PropsWithChildren<{}>) => {
  const web3Interface = useWeb3React<EthersWeb3Provider>()

  const activate: Web3ContextType['activate'] = useCallback(
    async (onError, throwErrors = false) => {
      web3Interface.activate(injectedConnector, onError, throwErrors)
    },
    [web3Interface]
  )

  return (
    <Web3Context.Provider
      value={{
        ...web3Interface,
        activate
      }}
    >
      {children}
    </Web3Context.Provider>
  )
}

const Web3Provider = ({ children }: PropsWithChildren<{}>) => (
  <Web3ReactProvider getLibrary={getLibrary}>
    <Web3RootProvider>{children}</Web3RootProvider>
  </Web3ReactProvider>
)

export default Web3Provider

export const useWeb3Context = () => useContext(Web3Context)
